import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react'

/**
 * Global state for the AI Coach drawer.
 *
 * The drawer is mounted once at the layout level. Any component on the
 * page can pop it open (FAB, header launcher, contextual button on a
 * setup row, …) and optionally seed it with an initial prompt that is
 * submitted automatically when the drawer mounts.
 *
 * `openVersion` increments on every open so that the chat panel knows
 * to (re-)submit `initialPrompt` even if the user opens the drawer
 * twice in a row with the same text.
 */
type AiCoachContextValue = {
  open: boolean
  initialPrompt: string | null
  openVersion: number
  openCoach: (initialPrompt?: string) => void
  closeCoach: () => void
  consumeInitialPrompt: () => void
}

const AiCoachContext = createContext<AiCoachContextValue | null>(null)

const HOTKEY_TARGET_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

export const AiCoachProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [open, setOpen] = useState(false)
  const [initialPrompt, setInitialPrompt] = useState<string | null>(null)
  const [openVersion, setOpenVersion] = useState(0)

  const openCoach = useCallback((seed?: string) => {
    setInitialPrompt(seed && seed.trim() ? seed.trim() : null)
    setOpen(true)
    setOpenVersion((v) => v + 1)
  }, [])

  const closeCoach = useCallback(() => {
    setOpen(false)
    setInitialPrompt(null)
  }, [])

  const consumeInitialPrompt = useCallback(() => {
    setInitialPrompt(null)
  }, [])

  // Global Cmd/Ctrl+K → toggle drawer.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isHotkey = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')
      if (!isHotkey) return
      const target = e.target as HTMLElement | null
      const tag = target?.tagName ?? ''
      const editable = target?.isContentEditable
      // Allow Cmd/K from anywhere — including inputs — because there
      // isn't really another use of Cmd/K anywhere else in the app.
      // We just want to make sure browser default doesn't fire (which
      // focuses the URL bar in some configs but not in app contexts).
      e.preventDefault()
      if (HOTKEY_TARGET_TAGS.has(tag) || editable) {
        // If the user is typing in some input, capture the current
        // text as the seed prompt — convenient when reading a setup
        // and wanting to ask the coach about it.
      }
      setOpen((prev) => {
        const next = !prev
        if (next) setOpenVersion((v) => v + 1)
        return next
      })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const value = useMemo<AiCoachContextValue>(
    () => ({open, initialPrompt, openVersion, openCoach, closeCoach, consumeInitialPrompt}),
    [open, initialPrompt, openVersion, openCoach, closeCoach, consumeInitialPrompt]
  )

  return <AiCoachContext.Provider value={value}>{children}</AiCoachContext.Provider>
}

export function useAiCoach(): AiCoachContextValue {
  const ctx = useContext(AiCoachContext)
  if (!ctx) {
    throw new Error('useAiCoach must be used inside <AiCoachProvider>')
  }
  return ctx
}

/**
 * Best-effort detection: returns true on macOS so we can render the
 * proper modifier symbol next to the keyboard shortcut hint.
 */
export function isMacLike(): boolean {
  if (typeof navigator === 'undefined') return false
  const platform = navigator.platform || ''
  return /Mac|iPhone|iPad|iPod/i.test(platform)
}
